import React from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiCheckCircle,FiAlertTriangle,FiDollarSign,FiShield,FiStar}=FiIcons;

const SocialProofSection=()=> {
  return (
    <section className="py-20 px-4 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-yellow-900/10 to-transparent"></div>
      
      {/* Animated Background Elements */}
      <div className="absolute top-10 left-10 w-32 h-32 bg-yellow-500/10 rounded-full blur-xl animate-pulse"></div>
      <div className="absolute bottom-10 right-10 w-40 h-40 bg-orange-500/10 rounded-full blur-xl animate-pulse delay-1000"></div>
      
      <div className="relative z-10 max-w-6xl mx-auto">
        <motion.div
          initial={{y: 30,opacity: 0}}
          whileInView={{y: 0,opacity: 1}}
          transition={{duration: 0.8}}
          viewport={{once: true}}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            <span className="bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
              เรื่องจริงที่เจ็บแต่จบสวย
            </span>
          </h2>
          <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto">
            "ทำไมผมถึงกล้าการันตี? เพราะวิชานี้ช่วยเซฟเงินผมไว้มหาศาล"
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Story Content */}
          <motion.div
            initial={{x: -50,opacity: 0}}
            whileInView={{x: 0,opacity: 1}}
            transition={{duration: 0.8}}
            viewport={{once: true}}
          >
            <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 backdrop-blur-sm border border-yellow-500/30 rounded-3xl p-8 relative overflow-hidden">
              {/* Decorative Elements */}
              <div className="absolute top-0 right-0 w-20 h-20 bg-yellow-500/20 rounded-full blur-2xl"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-orange-500/20 rounded-full blur-2xl"></div>
              
              <div className="relative z-10">
                <div className="flex items-center mb-6">
                  <SafeIcon icon={FiStar} className="text-3xl text-yellow-400 mr-3" />
                  <h3 className="text-2xl md:text-3xl font-bold text-white">เคสจริงจาก อ.รวิ</h3>
                </div>
                
                <div className="space-y-6 text-gray-300">
                  <div className="bg-slate-800/50 rounded-xl p-4 border-l-4 border-yellow-500">
                    <p className="leading-relaxed">
                      มีครั้งหนึ่ง... ผมเจอคนที่ดู <span className="text-green-400 font-semibold">"Perfect"</span> มากๆ มาเสนอโอกาสทางธุรกิจ
                    </p>
                  </div>
                  
                  <div className="bg-slate-800/50 rounded-xl p-4">
                    <p className="leading-relaxed mb-2">
                      📌 โปรไฟล์ดี พูดจาน่าเชื่อถือ
                    </p>
                    <p className="leading-relaxed">
                      📌 บุคลิกดูไว้วางใจได้สุดๆ
                    </p>
                    <p className="text-yellow-300 font-semibold mt-3">
                      ถ้าใช้ความรู้สึกตัดสิน... ผมคงเซ็นสัญญาและโอนเงินไปแล้ว
                    </p>
                  </div>
                  
                  <div className="bg-red-500/10 border-2 border-red-500/30 rounded-xl p-6">
                    <div className="flex items-center mb-3">
                      <SafeIcon icon={FiAlertTriangle} className="text-red-400 mr-2" />
                      <p className="text-red-300 font-medium text-lg">แต่ผมเอะใจ... เลยลองเปิด Bazi ของเขาดู</p>
                    </div>
                    <p className="text-white">
                      สิ่งที่เห็นในดวงคือ <span className="text-red-400 font-bold">"ความสัมพันธ์แบบอันตราย"</span> และมีเกณฑ์ของการเบียดเบียนซ่อนอยู่
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Results */}
          <motion.div
            initial={{x: 50,opacity: 0}}
            whileInView={{x: 0,opacity: 1}}
            transition={{duration: 0.8,delay: 0.2}}
            viewport={{once: true}}
          >
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-red-500/20 to-orange-500/20 backdrop-blur-sm border-2 border-red-500/30 rounded-3xl p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-16 h-16 bg-red-500/20 rounded-full blur-xl"></div>
                
                <div className="relative z-10">
                  <h4 className="text-2xl md:text-3xl font-bold text-white mb-6 flex items-center">
                    <SafeIcon icon={FiAlertTriangle} className="text-red-400 mr-3" />
                    ผลสุดท้ายรู้ไหมครับ?
                  </h4>
                  
                  <div className="space-y-4 text-gray-300 mb-6">
                    <div className="flex items-start bg-slate-800/50 rounded-lg p-3">
                      <span className="text-red-400 text-2xl mr-3">💥</span>
                      <p>มีการโกงและการหักหลังเกิดขึ้นจริงๆ ตามที่ดวงบอกเป๊ะ!</p>
                    </div>
                    
                    <div className="flex items-start bg-slate-800/50 rounded-lg p-3">
                      <span className="text-red-400 text-2xl mr-3">🔥</span>
                      <p>โปรเจคนั้นล่มไม่เป็นท่า...</p>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-2 border-green-500/50 rounded-2xl p-6">
                    <div className="flex items-start mb-4">
                      <span className="text-green-400 text-3xl mr-3">✨</span>
                      <div>
                        <p className="text-green-300 font-bold text-lg mb-2">แต่โชคดีในโชคร้าย...</p>
                        <p className="text-white leading-relaxed">
                          เพราะผม <span className="font-bold text-green-400">"รู้ทัน"</span> และวางแผนป้องกันไว้ตั้งแต่เห็นดวงเขา 
                          ผมจึงไม่สูญเสียเงินลงทุนเลยแม้แต่บาทเดียว
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 backdrop-blur-sm border-2 border-green-500/30 rounded-3xl p-8">
                <h4 className="text-xl font-bold text-white mb-6 flex items-center">
                  <span className="text-green-400 text-3xl mr-3">💰</span>
                  คุณค่าที่แท้จริง
                </h4>
                
                <div className="space-y-4 text-gray-300">
                  <p className="leading-relaxed">
                    ลองคิดดูสิครับ... ถ้าวันนั้นผมไม่มี Bazi 
                    ผมคงทุ่มหมดหน้าตัก และคงหมดตัวไปแล้ว
                  </p>
                  
                  <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-2 border-yellow-500/30 rounded-xl p-4">
                    <p className="text-green-300 font-bold text-xl text-center">
                      นี่คือราคาของคำว่า <span className="text-white">"รู้ก่อน"</span> ครับ... 
                      มันประเมินค่าไม่ได้จริงๆ
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{y: 30,opacity: 0}}
          whileInView={{y: 0,opacity: 1}}
          transition={{duration: 0.8,delay: 0.4}}
          viewport={{once: true}}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 backdrop-blur-sm border-2 border-yellow-500/30 rounded-3xl p-8 max-w-4xl mx-auto relative overflow-hidden">
            {/* Animated Background */}
            <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/10 via-orange-500/10 to-red-500/10 animate-gradient"></div>
            
            <div className="relative z-10">
              <h4 className="text-2xl md:text-4xl font-bold text-white mb-6">
                การลงทุนครั้งนี้... <span className="text-yellow-400">ถูกกว่าค่า "เลิกจ้าง"</span> หรือค่า <span className="text-orange-400">"หย่าร้าง"</span> หลายร้อยเท่า
              </h4>
              
              <div className="bg-slate-800/50 border-2 border-red-500/30 rounded-2xl p-6 mb-6">
                <p className="text-xl text-gray-300 mb-4">🚨 อย่ารอให้เจ็บตัวก่อนค่อยมาเรียนรู้</p>
                <p className="text-lg text-red-400 font-semibold">เพราะบทเรียนราคาแพงที่สุด คือ "การไม่รู้"</p>
              </div>
              
              <button className="group relative px-12 py-6 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white font-bold text-xl rounded-full shadow-2xl hover:shadow-yellow-500/30 transition-all duration-300 transform hover:scale-105 border-2 border-yellow-400/50">
                <span className="relative z-10">
                  🔥 สมัครทันที 9,995.- (รับโบนัสครบชุด)
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-yellow-600 via-orange-600 to-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {/* Animated Ring */}
                <div className="absolute inset-0 rounded-full border-2 border-white/30 animate-ping"></div>
              </button>
              
              <p className="text-yellow-300 mt-6 font-semibold text-lg">
                ⏰ รับจำนวนจำกัด เพื่อการดูแลทั่วถึง
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SocialProofSection;